import { useEffect, useRef, useState } from 'react'
import { closeSnackbar } from 'notistack'
import {
  isNotificationRead,
  useGetUnreadNotificationsCount,
} from '@/service-library/hooks/notifications'
import { getList } from '@/service-library/request-wrappers'
import { Notification } from '@/types/notifications'
import { showCustomSnackbar } from '@/utils/snackbars'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import NotificationPopup from './NotificationPopup'

function isPageActive() {
  return document.visibilityState === 'visible' && document.hasFocus()
}

function useIsPageActive() {
  const [isActive, setIsActive] = useState(isPageActive)

  useEffect(() => {
    function handleChange() {
      setIsActive(isPageActive())
    }
    document.addEventListener('visibilitychange', handleChange)
    window.addEventListener('focus', handleChange)
    window.addEventListener('blur', handleChange)
    return () => {
      document.removeEventListener('visibilitychange', handleChange)
      window.removeEventListener('focus', handleChange)
      window.removeEventListener('blur', handleChange)
    }
  }, [])

  return isActive
}

const MAX_POPUPS_PER_CHECK = 3

// Straight from pd-notify, skipping the query cache. Each load is compared
// with the last one, and a cached answer would hide new notifications.
function fetchNewestNotifications() {
  return getList<Notification>({
    url: '/v2/pd-notify/data/notifications/current-user',
    filters: { limit: '20' },
  }).then(({ data }) => data.results)
}

function showPopup(notification: Notification) {
  showCustomSnackbar(
    (key) => <NotificationPopup id={key} notification={notification} />,
    {
      key: notification.id,
      anchorOrigin: { vertical: 'top', horizontal: 'right' },
      autoHideDuration: 8_000,
    },
  )
}

/**
 * Pops up each new notification that arrives while this tab is in view
 * and its window has focus.
 *
 * The server cannot tell the page about a new notification. So this hook
 * watches the unread count. The count is fetched again every minute, and
 * right away when a push reaches the focused tab. When the count goes up,
 * the hook loads the newest notifications and pops up the ones it has not
 * seen before. If more than MAX_POPUPS_PER_CHECK are new, only the newest
 * of them get pop-ups.
 *
 * It learns which notifications already exist, without pop-ups, when the
 * page loads, when the tab comes back into view, when its window gets
 * focus again, and when the account changes. If that first load fails, it
 * learns on the next rise in the count instead. Notifications that arrive
 * while the user is elsewhere only count toward the bell badge.
 *
 * While the notifications panel is open, nothing pops up, because the
 * panel already lists them. Opening the panel closes any pop-ups showing.
 * Pop-ups from the old account are closed when the account changes.
 */
export default function useNotificationPopups({
  isPanelOpen,
}: {
  isPanelOpen: boolean
}) {
  const isActive = useIsPageActive()
  const { currentAccount } = useCurrentAccountContext()
  const { unreadCount, isSuccess } = useGetUnreadNotificationsCount()
  const seenIds = useRef<Set<string> | undefined>(undefined)
  const lastUnreadCount = useRef<number | undefined>(undefined)
  const resetCount = useRef(0)
  const shownPopupIds = useRef(new Set<string>())
  // The load below can finish after the panel opens or closes. A ref lets it
  // see whether the panel is open when the load ends, not when it began.
  const isPanelOpenRef = useRef(isPanelOpen)

  useEffect(() => {
    isPanelOpenRef.current = isPanelOpen
    if (!isPanelOpen) return
    shownPopupIds.current.forEach((id) => closeSnackbar(id))
    shownPopupIds.current.clear()
  }, [isPanelOpen])

  // After an account switch, a pop-up from the old account would still be on
  // screen. Clicking it would try to mark its notification read as the new
  // account, and that fails. So close the old account's pop-ups on a switch.
  useEffect(() => {
    const shown = new Set<string>()
    shownPopupIds.current = shown
    return () => shown.forEach((id) => closeSnackbar(id))
  }, [currentAccount?.id])

  useEffect(() => {
    resetCount.current += 1
    seenIds.current = undefined
    lastUnreadCount.current = undefined
    if (!isActive) return undefined

    let isCancelled = false
    fetchNewestNotifications()
      .then((notifications) => {
        if (!isCancelled) {
          seenIds.current = new Set(notifications.map(({ id }) => id))
        }
      })
      .catch((error) => {
        console.error('Could not load notifications for pop-ups', error)
      })
    return () => {
      isCancelled = true
    }
  }, [currentAccount?.id, isActive])

  useEffect(() => {
    if (!isActive || !isSuccess) return
    const previousCount = lastUnreadCount.current
    lastUnreadCount.current = unreadCount
    if (previousCount === undefined || unreadCount <= previousCount) return
    const resetCountAtStart = resetCount.current

    fetchNewestNotifications()
      .then((notifications) => {
        // While this was loading, the tab lost view or focus, or the account
        // changed. The first effect then started over, so this answer is out
        // of date. It counts its restarts so this check still works when
        // seenIds was empty both before and after.
        if (resetCount.current !== resetCountAtStart) return
        const seen = seenIds.current
        // seenIds is empty because the first load failed or hasn't come back.
        // Treat this load as the first one: remember these ids, no pop-ups.
        if (!seen) {
          seenIds.current = new Set(notifications.map(({ id }) => id))
          return
        }
        if (!isPanelOpenRef.current) {
          notifications
            .filter(
              (notification) =>
                !seen.has(notification.id) && !isNotificationRead(notification),
            )
            // The list comes newest first, so this keeps the newest few.
            .slice(0, MAX_POPUPS_PER_CHECK)
            // notistack adds each pop-up below the ones already showing.
            // Reversing stacks these in the order they arrived, the same way
            // pop-ups from later checks land below earlier ones.
            .reverse()
            .forEach((notification) => {
              showPopup(notification)
              shownPopupIds.current.add(notification.id)
            })
        }
        notifications.forEach(({ id }) => seen.add(id))
      })
      .catch((error) => {
        console.error('Could not load notifications for pop-ups', error)
      })
  }, [currentAccount?.id, isSuccess, isActive, unreadCount])
}
